// The signed-in account, kept in the store. The browser remembers the session id and user id; the
// secret session cookie is not readable here.
import api from '@/api/api';
import store from '@/store';

const KEY = 'slowgo.session';

function remember(sessionId, userId) {
  try {
    if (sessionId) localStorage.setItem(KEY, JSON.stringify({ sessionId, userId }));
    else localStorage.removeItem(KEY);
  } catch (e) { /* storage unavailable: stays signed in for this page only */ }
}

function remembered() {
  try { return JSON.parse(localStorage.getItem(KEY)) || null; } catch (e) { return null; }
}

function setUser(user, sessionId) {
  store.commit('set', { user: user || null, sessionId: sessionId || null });
}

export default {
  // Checks the remembered session with the server and loads its account.
  async load() {
    const r = remembered();
    if (r && r.sessionId) {
      try {
        const s = await api.call('GET', `sessions/${r.sessionId}`);
        const u = await api.call('GET', `users/${s.userId}`);
        setUser(u, r.sessionId);
      } catch (e) {
        remember(null);
        setUser(null);
      }
    }
    store.commit('set', { authChecked: true });
  },

  async signedIn(sessionId, userId) {
    remember(sessionId, userId);
    const u = await api.call('GET', `users/${userId}`);
    setUser(u, sessionId);
  },

  async signIn(email, password) {
    const s = await api.call('POST', 'sessions', { email, password });
    await this.signedIn(s.id, s.userId);
  },

  async register(email, displayName, password) {
    return (await api.call('POST', 'registrations', { email, displayName, password })).id;
  },

  async verifyRegistration(id, code) {
    const r = await api.call('POST', `registrations/${id}/verification`, { code });
    await this.signedIn(r.sessionId, r.userId);
  },

  async requestPasswordReset(email) {
    return (await api.call('POST', 'password-resets', { email })).id;
  },

  async verifyPasswordReset(id, code, password) {
    const r = await api.call('POST', `password-resets/${id}/verification`, { code, password });
    await this.signedIn(r.sessionId, r.userId);
  },

  async changePassword(currentPassword, password) {
    const u = store.state.user;
    await api.call('POST', 'password-resets', { email: u.email, currentPassword, password });
  },

  async setDisplayName(displayName) {
    const u = await api.call('PATCH', `users/${store.state.user.id}`, { displayName });
    setUser(u, store.state.sessionId);
  },

  async signOut() {
    const id = store.state.sessionId;
    try { if (id) await api.call('POST', `sessions/${id}/sign-out`, {}); } catch (e) { /* already ended */ }
    this.forget();
  },

  // Calls `onSignedIn` when another tab of this browser signs in (e.g. through an emailed link).
  // Returns a function that stops watching.
  watchOtherTabs(onSignedIn) {
    const handler = async e => {
      if (e.key !== KEY || !e.newValue) return;
      await this.load();
      if (store.state.user) onSignedIn();
    };
    window.addEventListener('storage', handler);
    return () => window.removeEventListener('storage', handler);
  },

  // Called when the server says the session is gone.
  forget() {
    remember(null);
    setUser(null);
  },
};
